import { Component, OnInit } from '@angular/core';
import { MenuItem, SelectItem } from 'primeng/api';
import { EcomService } from 'src/app/ecommerce/shared/ecom.service';
import { EcomTransactionService } from 'src/app/revenue/shared/ecommerce/ecom-transaction.service';
import { DatagridService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { UserService } from 'src/app/users/shared/user.service';
import { EcomTransactionSearchDto } from '../../shared/ecommerce/ecom-transaction-search.dto';
import { EcomTransactionDto } from '../../shared/ecommerce/ecom-transaction.dto';
import { InvoiceService } from '../../shared/invoice/invoice.service';
import { RevenueItem } from '../../shared/revenue-items/revenue-item.model';
import { RevenueItemService } from '../../shared/revenue-items/revenue-item.service';
import { debounce } from 'lodash-decorators';

@Component({
  selector: 'ecom-transactions',
  templateUrl: './ecommerce-transactions.component.html',
  styleUrls: ['./ecommerce-transactions.component.less'],
    standalone: false
})
export class EcomTransactionsComponent
  extends LazySelectableTableComponent<EcomTransactionSearchDto, EcomTransactionDto>
  implements OnInit
{
  loading = false;
  rowMenuItems = new Map<number, MenuItem[]>();
  revItemSearchResult: Array<RevenueItem> = [];
  activeRevenueItems: Array<RevenueItem> = [];
  selectedRevenueItems: Array<RevenueItem> = [];
  ecommerceTypes: SelectItem[] = [];
  actionsOptions: MenuItem[];
  exporting = false;
  isDirty = false;

  constructor(
    private _invoiceService: InvoiceService,
    datagridService: DatagridService,
    private _ecomTransactionService: EcomTransactionService,
    private _translationService: TranslationService,
    private _revenueItemService: RevenueItemService,
    private _ecomService: EcomService,
    private _navigationService: NavigationService,
    private _userService: UserService
  ) {
    super('LineItemId', datagridService);
    this.searchParams = { OrderBy: 'InvoiceNum:Desc', PageSize: 20 } as EcomTransactionSearchDto;
  }

  ngOnInit(): void {
    this._revenueItemService.getRevenueItems().then((data) => {
      this.activeRevenueItems = data;
    });

    this._ecomService.GetItemTypes().then((data) => {
      this.ecommerceTypes = data.Result.map<SelectItem>((x) => {
        return { label: x.Ecom_ItemType, value: x.Ecom_ItemTypeID };
      });
    });

    this.populateActions();
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  clearSearch() {
    this.selectedRevenueItems = [];
    this.searchParams = { OrderBy: 'InvoiceNum:Desc' } as EcomTransactionSearchDto;
    this.isDirty = false;
  }

  fetchResults(): Promise<PagedResultGeneric<EcomTransactionDto>> {
    return this._ecomTransactionService.search(this.searchParams).then((data) => {
      for (let transaction of data.Result) {
        transaction.DateSent = new Date(transaction.DateSent);
        if (transaction.DateSent.getFullYear() < 1900) {
          transaction.DateSent = null;
        }
      }
      return data;
    });
  }

  async dateSentChanged(transaction: EcomTransactionDto) {
    let invoice = await this._invoiceService.getInvoiceUpsertDto(transaction.InvoiceNum, true);
    if (invoice.InvoiceHeader?.DateSent.toDateString() != transaction.DateSent?.toDateString()) {
      invoice.InvoiceHeader.DateSent = transaction.DateSent;
      if (transaction.DateSent && !invoice.InvoiceHeader.SentBy) {
        invoice.InvoiceHeader.SentBy = (await this._userService.getCurrentUser()).UserName;
      } else if (!transaction.DateSent) {
        // let them edit the sent date without changing the sent-by
        invoice.InvoiceHeader.SentBy = '';
      }
      this._invoiceService.upsertInvoice(invoice);
      this.refresh();
    }
  }

  @debounce(2000)
  refresh() {
    // just wrapping search with a debounc so users can make multiple changes without having to wait to search between each change
    this.search();
  }

  editInvoice(id: number) {
    this._navigationService.navigateToInvoiceEdit(id);
  }

  populateActions() {
    this.actionsOptions = [
      {
        label: this._translationService.getInstantTitlecase('revenue.ecommerce-transactions.mark-selected-as-sent'),
        command: async (event) => {
          let ids = await this.getSelectedInvoiceNums();
          this._ecomTransactionService.MarkInvoicesAsSent(ids).then((x) => {
            this.refresh();
          });
        },
      },
      {
        label: this._translationService.getInstantTitlecase('revenue.ecommerce-transactions.view-selected-invoices'),
        command: async (event) => {
          let ids = await this.getSelectedInvoiceNums();
          this._navigationService.navigateToInvoiceReportForMultipleNumbers(ids);
        },
      },
    ];
  }

  private async getSelectedInvoiceNums(): Promise<Array<number>> {
    if (!this.selectAll) {
      const ids = [];
      for (const pair of this.selectedMap.entries()) {
        if (pair[1]) {
          ids.push(pair[0]);
        }
      }
      return this.searchResult.Result.filter((x) => ids.some((id) => x.LineItemId == id)).map((x) => x.InvoiceNum);
    } else {
      const copiedParams: EcomTransactionSearchDto = JSON.parse(JSON.stringify(this.searchParams));
      this.searchParams.PageSize = 9999;
      return this.fetchResults()
        .then((result) => {
          // filter out any deselected IDs
          let filtered = result.Result.filter((x) => !this.selectedMap.has(x) || this.selectedMap.get(x));
          return filtered.map<number>((x) => x.InvoiceNum);
        })
        .finally(() => {
          this.searchParams = copiedParams;
        });
    }
  }

  sentChanged(transaction: EcomTransactionDto) {
    if (transaction.Sent) {
      transaction.DateSent = new Date(new Date().toJSON().slice(0, 10));
    } else {
      transaction.DateSent = null;
    }
    this.dateSentChanged(transaction);
  }

  rowActions(transaction: EcomTransactionDto): MenuItem[] {
    if (!this.rowMenuItems.has(transaction.LineItemId)) {
      this.rowMenuItems.set(transaction.LineItemId, [
        {
          label: this._translationService.getInstantTitlecase('revenue.ecommerce-transactions.mark-as-sent'),
          command: () => this.sentChanged(transaction),
        },
        {
          label: this._translationService.getInstantTitlecase('revenue.ecommerce-transactions.edit-invoice'),
          command: () => this.editInvoice(transaction.InvoiceNum),
        },
        {
          label: this._translationService.getInstantTitlecase('revenue.ecommerce-transactions.view-invoice'),
          command: () => this.viewInvoice(transaction),
        },
      ]);
    }
    return this.rowMenuItems.get(transaction.LineItemId);
  }

  viewInvoice(transaction: EcomTransactionDto): void {
    this._navigationService.navigateToInvoiceReport(transaction.InvoiceNum);
  }

  searchRevenueItems(event: any) {
    this.revItemSearchResult = this.activeRevenueItems.filter(
      (x) =>
        !this.selectedRevenueItems.some((ri) => ri.ItemNum == x.ItemNum) && x.ItemName.toLowerCase().includes(event.query.toLowerCase())
    );
  }

  searchClicked() {
    if (this.selectedRevenueItems.length) {
      this.searchParams.RevenueItemNums = this.selectedRevenueItems.map((x) => x.ItemNum);
    } else {
      this.searchParams.RevenueItemNums = null;
    }
    this.search();
  }

  exportClicked() {
    this.exporting = true;
    this._ecomTransactionService
      .exportToCsv(this.searchParams)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .finally(() => {
        this.exporting = false;
      });
  }

  navigateToEcomSpa() {
    this._navigationService.navigateToEcomSpa();
  }
}
